/**
 * Message query operations - Get
 */

import type { ChatId, MessageRole } from '../../types.js';
import { getSupabaseClient } from '../../supabase-client.js';
import type { StoredMessage, QueryOptions, QueryResult } from '../types.js';
import { logger } from '../../../logger.js';

/**
 * Get a single message by ID
 */
export async function getMessageById(messageId: string): Promise<StoredMessage | null> {
  try {
    const client = getSupabaseClient();
    const { data, error } = await client
      .from('meridian_messages')
      .select('*')
      .eq('id', messageId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows found
        return null;
      }
      throw error;
    }

    return data as StoredMessage;
  } catch (error) {
    logger.error('error getting message by id', error, { op: 'getMessageById' });
    throw error;
  }
}

/**
 * Get all messages for a chat
 */
export async function getMessagesByChat(
  chatId: ChatId,
  options?: QueryOptions
): Promise<QueryResult<StoredMessage>> {
  try {
    const client = getSupabaseClient();
    const limit = options?.limit || 100;
    const offset = options?.offset || 0;
    const ascending = options?.ascending ?? true;

    const { data, error, count } = await client
      .from('meridian_messages')
      .select('*', { count: 'exact' })
      .eq('chat_id', chatId)
      .order('timestamp', { ascending })
      // `timestamp` alone was not a total order: `saveChat` used to stamp one
      // value across a whole turn, so a user prompt and its reply tied and
      // Postgres could return them in either order, differently between two
      // identical queries. `saveChat` now stamps per-message, which fixes the
      // ORDER; this second key fixes DETERMINISM for the rows already written
      // under the old behaviour. The PK is a uuid4, so it does not encode
      // insertion order — it only guarantees that a tie resolves the same way
      // every time, which is what stops a transcript reshuffling on reload.
      .order('id', { ascending })
      .range(offset, offset + limit - 1);

    if (error) {
      throw error;
    }

    const items = (data || []) as StoredMessage[];
    const hasMore = count ? offset + limit < count : false;

    return {
      items,
      hasMore,
      offset,
    };
  } catch (error) {
    logger.error('error getting messages by chat', error, { op: 'getMessagesByChat' });
    throw error;
  }
}
